import { ReactNode, useId } from "react";
import { Globe } from "lucide-react";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import "flag-icons/css/flag-icons.min.css";

export const LEVELS: Record<string, { label: string; color: string }> = {
  low: { label: "LOW", color: "#2462e5" },
  moderate: { label: "MODERATE", color: "#14cfe6" },
  high: { label: "HIGH", color: "#8b5cff" },
  very_high: { label: "VERY HIGH", color: "#f33db4" },
};

const FLAG_CODE: Record<string, string> = {
  US: "us", EU: "eu", UK: "gb", CN: "cn", IN: "in", JP: "jp",
  SG: "sg", KR: "kr", HK: "hk", AE: "ae", BR: "br", RU: "ru",
};

export const Flag = ({ code, className = "w-7 h-5" }: { code: string; className?: string }) => {
  const iso = FLAG_CODE[code] || (/^[A-Z]{2}$/.test(code) && code !== "GL" ? code.toLowerCase() : null);
  if (!iso) {
    return (
      <span aria-hidden className={`inline-flex items-center justify-center rounded-[3px] bg-[#101018] ring-1 ring-white/10 text-[#8fa2c8] shrink-0 ${className}`}>
        <Globe className="w-[60%] h-[60%]" />
      </span>
    );
  }
  return (
    <span aria-hidden
      className={`fi fi-${iso} inline-block rounded-[3px] ring-1 ring-white/15 shadow-[0_1px_4px_rgba(0,0,0,0.5)] shrink-0 ${className}`}
      style={{ backgroundSize: "cover", backgroundPosition: "center" }} />
  );
};

export const STAGE_META: Record<string, { label: string; color: string }> = {
  proposed: { label: "PROPOSED", color: "#b15cff" },
  in_review: { label: "IN REVIEW", color: "#3a8cff" },
  in_committee: { label: "IN COMMITTEE", color: "#0dd6eb" },
  voting: { label: "VOTING / APPROVAL", color: "#f5b420" },
  passed: { label: "PASSED / ACTIVE", color: "#22d38f" },
};

export const IMPACT_META: Record<string, { label: string; color: string; dots: number }> = {
  critical: { label: "Critical", color: "#ff5bd6", dots: 3 },
  high: { label: "High", color: "#c36bff", dots: 2 },
  medium: { label: "Medium", color: "#f5b420", dots: 1 },
  low: { label: "Low", color: "#22d38f", dots: 0 },
};

export const PANEL_STYLE = {
  background: "linear-gradient(180deg, #08080d 0%, #030305 100%)",
  borderColor: "rgba(124,112,178,0.26)",
};
export const PANEL = "rounded-2xl border";
export const CARD_STYLE = {
  background: "linear-gradient(180deg, #0a0a10 0%, #050507 100%)",
  borderColor: "rgba(255,255,255,0.07)",
};

export const LevelChip = ({ level, className = "" }: { level: string; className?: string }) => {
  const meta = LEVELS[level] || LEVELS.low;
  return (
    <span className={`inline-flex items-center justify-center rounded-md px-2.5 h-[22px] nl-geist font-bold text-[9px] tracking-[0.08em] whitespace-nowrap ${className}`}
      style={{ color: meta.color, backgroundColor: `${meta.color}1f`, boxShadow: `inset 0 0 0 1px ${meta.color}55` }}>
      {meta.label} ACTIVITY
    </span>
  );
};

export const InfoTip = ({ label, children, testid }: { label: string; children: ReactNode; testid?: string }) => (
  <Tooltip delayDuration={150}>
    <TooltipTrigger asChild>
      <button type="button" aria-label={label} data-testid={testid}
        className="inline-flex items-center justify-center w-6 h-6 -m-[5px] text-[#78829c] hover:text-white focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#8b5cff] rounded motion-safe:transition-colors shrink-0">
        <span aria-hidden className="font-serif italic font-semibold text-[11px] leading-none">i</span>
      </button>
    </TooltipTrigger>
    <TooltipContent side="top" className="max-w-[280px] bg-[#0a0a10] border border-[#2c2c3e] text-[#c9cfdd] text-[11px] leading-snug nl-geist px-3 py-2 rounded-lg shadow-2xl">
      {children}
    </TooltipContent>
  </Tooltip>
);

export const AreaSpark = ({ points, color, className = "w-full h-8" }: { points: number[]; color: string; className?: string }) => {
  const gid = useId().replace(/:/g, "");
  if (!points || points.length < 2 || points.every((p) => p === 0)) {
    return <div className={`${className} flex items-end`} aria-hidden><span className="nl-geist text-[8px] text-[#4c5670]">no activity yet</span></div>;
  }
  const W = 160, H = 40;
  const min = Math.min(...points), max = Math.max(...points);
  const span = max - min || 1;
  const pts = points.map((v, i) =>
    [((i / (points.length - 1)) * W), (H - 3 - ((v - min) / span) * (H - 8))] as [number, number]);
  const line = pts.map((p) => p.join(",")).join(" ");
  const area = `0,${H} ${line} ${W},${H}`;
  return (
    <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden className={className}>
      <defs>
        <linearGradient id={`as-${gid}`} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={color} stopOpacity="0.36" />
          <stop offset="100%" stopColor={color} stopOpacity="0" />
        </linearGradient>
      </defs>
      <polygon points={area} fill={`url(#as-${gid})`} />
      <polyline points={line} fill="none" stroke={color} strokeWidth="1.6"
        strokeLinecap="round" strokeLinejoin="round" strokeOpacity="0.95" />
    </svg>
  );
};

export const deltaTone = (v: number | null | undefined) =>
  v == null ? "#7a86a0" : v > 0 ? "#22d38f" : v < 0 ? "#14cfe6" : "#7a86a0";

const NATION_COLORS: Record<string, string[]> = {
  US: ["#e0162b", "#e8ecf5", "#3c6df0"],
  EU: ["#2456e6", "#ffd617"],
  UK: ["#c8102e", "#e8ecf5", "#3757c4"],
  CN: ["#de2910", "#ffde00"],
  IN: ["#ff9933", "#e8ecf5", "#138808"],
  JP: ["#d7063b", "#e8ecf5"],
  SG: ["#ed2939", "#e8ecf5"],
  KR: ["#cd2e3a", "#e8ecf5", "#0047a0"],
  HK: ["#de2910", "#e8ecf5"],
  AE: ["#ef3340", "#e8ecf5", "#009739"],
  BR: ["#009739", "#ffdf00", "#002776"],
  RU: ["#e8ecf5", "#0039a6", "#d52b1e"],
  AU: ["#00247d", "#e8ecf5", "#e4002b"],
  CA: ["#d80621", "#e8ecf5"],
  DE: ["#dd0000", "#ffce00"],
  FR: ["#0055a4", "#e8ecf5", "#ef4135"],
  CH: ["#da291c", "#e8ecf5"],
  NG: ["#008751", "#e8ecf5"],
  ZA: ["#007a4d", "#ffb612", "#de3831"],
  TR: ["#e30a17", "#e8ecf5"],
  AR: ["#74acdf", "#e8ecf5", "#f6b40e"],
  MX: ["#006341", "#e8ecf5", "#ce1126"],
  TH: ["#a51931", "#e8ecf5", "#2d2a4a"],
  PH: ["#0038a8", "#ce1126", "#fcd116"],
  VN: ["#da251d", "#ffde00"],
  ID: ["#ce1126", "#e8ecf5"],
  MY: ["#cc0001", "#e8ecf5", "#010066"],
  IL: ["#0038b8", "#e8ecf5"],
  SA: ["#165d31", "#e8ecf5"],
  KZ: ["#00afca", "#fec50c"],
  PK: ["#01411c", "#e8ecf5"],
  SV: ["#0f47af", "#e8ecf5"],
  TW: ["#fe0000", "#000095", "#e8ecf5"],
  NZ: ["#00247d", "#cc142b", "#e8ecf5"],
  UA: ["#0057b7", "#ffd700"],
  KE: ["#006600", "#bb0000", "#e8ecf5"],
};
export const nationGradient = (code: string) =>
  (NATION_COLORS[code] || ["#8b5cff", "#14cfe6"]).join(", ");
